import { session } from "../auth/session";
import { apiRequest } from "./api";
import { getDeploymentConfig } from "./deployment-config";
import { fetchWithLogging } from "./fetch-logger";

// Settings that follow the account to every browser and device, kept by
// play-api in DynamoDB (GET/PUT /preferences) as the localStorage entries
// they already are. The browser's copy stays the one the app reads, so
// playing signed out works as before. At load (main.tsx, before render) a
// signed-in browser takes the account's copy, unless it changed something
// that never reached the cloud, which goes up instead; each change after
// that is sent a second later. Whose unsent change it is matters, as with
// saves (emulator/profiles.ts): one made signed out is adopted by the next
// account to sign in, laid over its copy; one made as another account
// never goes up over this one's. Device things (the guide's width, whether
// it is open) stay in the browser.

const KEYS = [
  "pocket-play.muted",
  "pocket-play.fastForward",
  "pocket-play.autoSave",
  "pocket-play.movement",
  "pocket-play.keymap",
  "pocket-play.librarySort",
  "pocket-play.theme",
];
const PREFIXES = [
  "pocket-play.cheats.",
  "pocket-play.n64Move.",
  "pocket-play.shoulders.",
  "pocket-play.controller.",
  "pocket-play.consoleController.",
];
/**
 * Set while a change hasn't reached the cloud: whose it is (the signed-in
 * account's sub, or null when made signed out) and, signed out, which
 * settings changed, so only those are laid over the next account's copy.
 */
const DIRTY_KEY = "pocket-play.prefsDirty";

interface Dirty {
  by: string | null;
  keys: string[];
}

function readDirty(): Dirty | null {
  try {
    const dirty = JSON.parse(localStorage.getItem(DIRTY_KEY) ?? "null") as Dirty | null;
    return dirty && typeof dirty === "object" && Array.isArray(dirty.keys) ? dirty : null;
  } catch {
    return null;
  }
}

const markDirty = (dirty: Dirty) => localStorage.setItem(DIRTY_KEY, JSON.stringify(dirty));
const PUSH_DELAY_MS = 1000;
const LOAD_TIMEOUT_MS = 2500;

export const followsAccount = (key: string) => KEYS.includes(key) || PREFIXES.some((p) => key.startsWith(p));

/**
 * Runs `listener` after the account's settings replace the browser's (for
 * modules that cache one). A window event, not a set of listeners here:
 * keymap.ts registers while this module is still loading (it imports this
 * module through the API and auth modules), and only a function declaration
 * is ready that early.
 */
export function onPreferencesApplied(listener: () => void): () => void {
  window.addEventListener("pocket-play:preferences-applied", listener);
  return () => window.removeEventListener("pocket-play:preferences-applied", listener);
}

/** Writes a setting (null removes it) and, for one that follows the account, sends it up. */
export function setPref(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
    if (!followsAccount(key)) return;
    const by = signedInSub();
    const earlier = readDirty();
    const keys = by ? [] : [...new Set([...(earlier && earlier.by === null ? earlier.keys : []), key])];
    markDirty({ by, keys });
  } catch {
    return;
  }
  schedulePush();
}

const signedInSub = () => session.getState().user?.sub || null;

function collect(): Record<string, string> {
  const prefs: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && followsAccount(key)) prefs[key] = localStorage.getItem(key) ?? "";
  }
  return prefs;
}

let timer: number | undefined;
function schedulePush() {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => void push(), PUSH_DELAY_MS);
}

async function push() {
  if (!session.bearer()) return;
  try {
    await apiRequest("/preferences", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prefs: collect() }),
    });
    localStorage.removeItem(DIRTY_KEY);
  } catch {
    // Still marked; the next change or load sends it.
  }
}

// At load: one try each with a short limit, rather than apiRequest's retry
// after a refresh (main.tsx waits for the session, so its tokens are as
// fresh as they get). The limit covers reading the answer too, not just its
// arrival.
function call(token: string, init: RequestInit = {}): Promise<Response> {
  return fetchWithLogging(
    `${getDeploymentConfig().apiUrl}/preferences`,
    { ...init, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...init.headers } },
    { timeoutMs: LOAD_TIMEOUT_MS },
  );
}

/**
 * Makes the browser's settings the signed-in account's. Never throws and
 * gives up after a couple of seconds: offline, the browser's copy is used.
 */
export async function settlePreferences(): Promise<void> {
  const token = session.bearer();
  if (!token) return;
  try {
    const sub = signedInSub();
    const dirty = readDirty();
    const mine = sub !== null && dirty?.by === sub;
    const send = () => call(token, { method: "PUT", body: JSON.stringify({ prefs: collect() }) });
    const cloud = mine ? null : await call(token).then((r) => (r.ok ? r.json() : null));
    if (mine || (cloud && !cloud.updatedAt)) {
      // Changed here and not sent yet, or the account has none: this browser's go up.
      if ((await send()).ok) localStorage.removeItem(DIRTY_KEY);
      return;
    }
    if (!cloud) return;
    // The account's copy, with what was changed signed out on top. Another
    // account's unsent change is dropped, so it can't go up later as theirs.
    const signedOut = dirty?.by === null ? dirty.keys.map((key) => [key, localStorage.getItem(key)] as const) : [];
    const prefs = cloud.prefs as Record<string, string>;
    for (const key of Object.keys(collect())) if (!(key in prefs)) localStorage.removeItem(key);
    for (const [key, value] of Object.entries(prefs)) if (followsAccount(key)) localStorage.setItem(key, value);
    for (const [key, value] of signedOut) {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    }
    localStorage.removeItem(DIRTY_KEY);
    window.dispatchEvent(new Event("pocket-play:preferences-applied"));
    if (
      signedOut.length &&
      !(await send().then(
        (r) => r.ok,
        () => false,
      ))
    )
      markDirty({ by: sub, keys: [] });
  } catch {
    // The browser's copy stands.
  }
}
